import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { getData } from "../../utils/api";

function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTimeIST(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const ManualPollHistoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pollHistory, setPollHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getData(`/data/webhook/manual-poll-history/${id}`)
      .then((res) => {
        if (res?.status && res?.data) setPollHistory(res.data);
        else setError("Failed to load history");
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err?.message || "Failed to load");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const failedIds = pollHistory?.failedOrderIds
    ? Array.isArray(pollHistory.failedOrderIds)
      ? pollHistory.failedOrderIds
      : []
    : [];
  const insertedIds = pollHistory?.insertedOrderIds
    ? Array.isArray(pollHistory.insertedOrderIds)
      ? pollHistory.insertedOrderIds
      : []
    : [];

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  if (error || !pollHistory) {
    return (
      <div className="p-6">
        <button
          type="button"
          onClick={() => navigate("/analytics/manual-polling")}
          className="text-gray-600 hover:text-gray-900 mb-4"
        >
          ← Back to Manual Polling
        </button>
        <p className="text-red-600">{error || "Not found"}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate("/analytics/manual-polling")}
          className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          ← Back to Manual Polling
        </button>
      </div>
      <h1 className="text-2xl font-bold mb-2">Manual Poll History #{pollHistory.id}</h1>
      <p className="text-sm text-gray-600 mb-6">
        Platform: <span className="font-medium capitalize">{pollHistory.platform}</span>
      </p>

      <h2 className="text-lg font-semibold mb-3">Details</h2>
      <div className="overflow-x-auto rounded border border-gray-200 mb-6">
        <table className="min-w-full bg-white text-sm">
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700 w-48">Date from</td>
              <td className="px-3 py-2 text-gray-900">
                {typeof pollHistory.dateFrom === "string" && pollHistory.dateFrom.length > 10 ? (
                  <span className="block space-y-1">
                    <span className="font-mono text-xs text-gray-600 block">
                      ISO: {pollHistory.dateFrom}
                    </span>
                    <span>IST: {formatDateTimeIST(pollHistory.dateFrom)}</span>
                  </span>
                ) : (
                  pollHistory.dateFrom
                )}
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Date to</td>
              <td className="px-3 py-2 text-gray-900">
                {typeof pollHistory.dateTo === "string" && pollHistory.dateTo.length > 10 ? (
                  <span className="block space-y-1">
                    <span className="font-mono text-xs text-gray-600 block">
                      ISO: {pollHistory.dateTo}
                    </span>
                    <span>IST: {formatDateTimeIST(pollHistory.dateTo)}</span>
                  </span>
                ) : (
                  pollHistory.dateTo
                )}
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Platform</td>
              <td className="px-3 py-2 capitalize text-gray-900">{pollHistory.platform}</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Status</td>
              <td className="px-3 py-2">
                <span
                  className={
                    pollHistory.status === "success"
                      ? "text-green-600"
                      : pollHistory.status === "failed"
                        ? "text-red-600"
                        : pollHistory.status === "cancelled"
                          ? "text-gray-600"
                          : "text-amber-600"
                  }
                >
                  {pollHistory.status}
                </span>
              </td>
            </tr>
            {pollHistory.requestId && (
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium text-gray-700">Request ID</td>
                <td className="px-3 py-2 text-gray-900 font-mono text-xs">{pollHistory.requestId}</td>
              </tr>
            )}
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Total orders fetched</td>
              <td className="px-3 py-2 text-gray-900">{pollHistory.totalOrdersFetched ?? "—"}</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Created</td>
              <td className="px-3 py-2 text-gray-900">{pollHistory.createdCount ?? "—"}</td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Skipped / failed count</td>
              <td className="px-3 py-2 text-gray-900">{pollHistory.skippedCount ?? "—"}</td>
            </tr>
            {pollHistory.maxPurchaseDate && (
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium text-gray-700">Max purchase date</td>
                <td className="px-3 py-2 text-gray-600">
                  {formatDateTimeIST(pollHistory.maxPurchaseDate)}
                </td>
              </tr>
            )}
            <tr className="border-b border-gray-200">
              <td className="px-3 py-2 font-medium text-gray-700">Triggered at</td>
              <td className="px-3 py-2 text-gray-600">
                {formatDateTimeIST(pollHistory.triggeredAt || pollHistory.createdAt)}
              </td>
            </tr>
            {pollHistory.errorMessage && (
              <tr className="border-b border-gray-200">
                <td className="px-3 py-2 font-medium text-gray-700">Error</td>
                <td className="px-3 py-2 text-red-600">{pollHistory.errorMessage}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="text-lg font-semibold mb-3">Inserted order IDs</h2>
      {insertedIds.length === 0 ? (
        <p className="text-sm text-gray-500 mb-6">No inserted orders.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200 mb-6">
          <table className="min-w-full bg-white text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-700">#</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Order ID</th>
              </tr>
            </thead>
            <tbody>
              {insertedIds.map((orderId, index) => (
                <tr key={orderId} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-gray-600">{index + 1}</td>
                  <td className="px-3 py-2 text-gray-900 font-mono">{orderId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-lg font-semibold mb-3">Failed order IDs</h2>
      {failedIds.length === 0 ? (
        <p className="text-sm text-gray-500 mb-6">No failed orders.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200 mb-6">
          <table className="min-w-full bg-white text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-700">#</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Order ID</th>
              </tr>
            </thead>
            <tbody>
              {failedIds.map((orderId, index) => (
                <tr key={orderId} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-gray-600">{index + 1}</td>
                  <td className="px-3 py-2 text-gray-900 font-mono">{orderId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManualPollHistoryDetail;
